// 설정 › 상황판 연결 — 만든 주소를 잠깐 보이고, 목록과 회수를 읽기 쉽게. 서버: work_manager /display-keys.
// squadev의 displayKeysModel에서 옮겼다(운영자 칸 → 로그인한 사람이 자기 프로젝트를 묶는 칸).
//
// 연결 주소는 이 화면에서 한 번만 보인다. 상태에만 두고 닫으면 지운다 — 캐시 · 저장소 · 주소에 넣지 않는다.

export const KEYS_PATH = "/display-keys";
/** 서버의 `MAX_NAME`과 같다. */
export const MAX_KEY_NAME = 60;
/** 서버의 `MAX_PROJECTS`와 같다. */
export const MAX_KEY_PROJECTS = 20;
const DAY_MS = 86_400_000;

export interface DisplayKeyRow {
  id: string;
  name: string;
  projectIds: string[];
  createdBy: string;
  createdAtMs: number;
  expiresAtMs: number;
  revokedAtMs: number | null;
  revokedBy: string | null;
  lastUsedAtMs: number | null;
  live: boolean;
}

export interface DisplayKeys {
  keys: DisplayKeyRow[];
}

export interface IssuedKey {
  key: DisplayKeyRow;
  /** 열쇠 원문 — 이 답에 한 번만 온다. */
  secret: string;
}

/** 벽 화면 주소 — 열쇠는 # 뒤에 둔다(서버로 가지 않고, 화면이 읽자마자 주소에서 지운다). */
export function displayLink(origin: string, secret: string): string {
  return `${origin}/display/#k=${secret}`;
}

export type CopyState = "none" | "copied" | "failed";

export type IssueState = { step: "idle"; problem: string | null } | { step: "sending" } | { step: "shown"; name: string; link: string; copy: CopyState };

export type IssueEvent =
  | { type: "sent" }
  | { type: "issued"; name: string; link: string }
  | { type: "failed"; problem: string }
  | { type: "copied" }
  | { type: "copy_failed" }
  | { type: "closed" };

export const NO_LINK: IssueState = { step: "idle", problem: null };

export function issueReducer(state: IssueState, event: IssueEvent): IssueState {
  switch (event.type) {
    case "sent":
      return { step: "sending" };
    case "issued":
      return { step: "shown", name: event.name, link: event.link, copy: "none" };
    case "failed":
      return { step: "idle", problem: event.problem };
    case "copied":
      return state.step === "shown" ? { ...state, copy: "copied" } : state;
    case "copy_failed":
      return state.step === "shown" ? { ...state, copy: "failed" } : state;
    case "closed":
      return NO_LINK;
  }
}

export function nameProblem(name: string): string | null {
  const trimmed = name.trim();
  if (!trimmed) return "이름을 적어 주세요";
  return Array.from(trimmed).length > MAX_KEY_NAME ? `이름은 ${MAX_KEY_NAME}자까지 씁니다` : null;
}

export function projectsProblem(projectIds: readonly string[]): string | null {
  if (projectIds.length === 0) return "보여 줄 프로젝트를 고르세요";
  return projectIds.length > MAX_KEY_PROJECTS ? `프로젝트는 ${MAX_KEY_PROJECTS}개까지 묶습니다` : null;
}

/** 주소가 보이는 동안에는 새로 만들지 않는다 — 새 주소가 덮으면 아직 안 넣은 주소가 사라진다. */
export function canIssue(state: IssueState, name: string, projectIds: readonly string[]): boolean {
  return state.step === "idle" && nameProblem(name) === null && projectsProblem(projectIds) === null;
}

export type KeyState = "live" | "revoked" | "expired";

export interface KeyRowView {
  id: string;
  name: string;
  projects: string;
  made: string;
  ends: string;
  lastUsed: string;
  state: KeyState;
  canRevoke: boolean;
}

const pad = (n: number) => String(n).padStart(2, "0");

function dayOf(ms: number): string {
  const d = new Date(ms);
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

export function ago(ms: number, now: number): string {
  const minutes = Math.floor((now - ms) / 60_000);
  if (minutes < 1) return "방금";
  if (minutes < 60) return `${minutes}분 전`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}시간 전`;
  const days = Math.floor(hours / 24);
  return days === 1 ? "어제" : `${days}일 전`;
}

function stateOf(key: DisplayKeyRow, now: number): KeyState {
  if (key.revokedAtMs !== null) return "revoked";
  return now < key.expiresAtMs ? "live" : "expired";
}

function endsText(key: DisplayKeyRow, state: KeyState, now: number): string {
  if (key.revokedAtMs !== null) return `${dayOf(key.revokedAtMs)} 회수`;
  if (state === "expired") return `${dayOf(key.expiresAtMs)} 끝남`;
  const days = Math.ceil((key.expiresAtMs - now) / DAY_MS);
  return `${dayOf(key.expiresAtMs)} · ${days}일 남음`;
}

/** 묶은 프로젝트의 이름 — 지금 내가 볼 수 없는 프로젝트는 '(볼 수 없음)'으로. */
function projectsText(ids: readonly string[], titles: ReadonlyMap<string, string>): string {
  return ids.map((id) => titles.get(id) ?? "(볼 수 없는 프로젝트)").join(", ");
}

/** 쓰는 연결부터, 최근에 만든 것부터 — 회수할지 살필 것이 위로 온다. */
export function keyRows(keys: readonly DisplayKeyRow[], titles: ReadonlyMap<string, string>, now: number): KeyRowView[] {
  return keys
    .map((key) => {
      const state = stateOf(key, now);
      const row: KeyRowView = {
        id: key.id,
        name: key.name,
        projects: projectsText(key.projectIds, titles),
        made: dayOf(key.createdAtMs),
        ends: endsText(key, state, now),
        lastUsed: key.lastUsedAtMs === null ? "접속 없음" : ago(key.lastUsedAtMs, now),
        state,
        canRevoke: state === "live",
      };
      return { row, createdAt: key.createdAtMs };
    })
    .sort((a, b) => Number(b.row.canRevoke) - Number(a.row.canRevoke) || b.createdAt - a.createdAt)
    .map((view) => view.row);
}

export function revokeQuestion(name: string): string {
  return `'${name}' 연결을 회수할까요? 이 주소로 켜 둔 화면이 바로 막힙니다.`;
}

/** 서버의 거절을 사람 말로 — 본문의 message를 쓰고, 없으면 상태로. */
export function failureText(error: unknown): string {
  const response = (error as { response?: { status?: number; data?: { message?: unknown; code?: unknown } } })?.response;
  if (response?.status === 403 || response?.data?.code === "1014") return "멤버인 프로젝트만 묶을 수 있습니다";
  if (typeof response?.data?.message === "string" && response.data.message.trim()) return response.data.message;
  if (response?.status) return `처리하지 못했습니다(HTTP ${response.status})`;
  return "서버에 닿지 못했습니다";
}
